/**
 * Shared Cyber Resilience summary card used across the reviewer PDF
 * (Section 04 — Top Findings & Recommendations, Section 08 — Risk
 * Distribution & Assessment Statistics). Keeps colour, tint and
 * typography identical between sections.
 */
import type jsPDF from "jspdf";
import { REPORT_THEME, type RGB } from "./theme";
import { BAND_COLORS, type MaturityBand } from "@/reports/telecom/cyberResilienceSummary";

export type ResilienceBand = MaturityBand | "Unrated";

function hexToRgb(hex: string): RGB {
  const m = hex.replace("#", "");
  const n = parseInt(m, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export interface CyberResilienceCardOptions {
  /** Optional small label printed under the band value. Default: "CYBER RESILIENCE". */
  label?: string;
  /** Show the thin accent strip at the top of the card. Default: true. */
  topAccent?: boolean;
  /** Font size for the band value. Default: auto based on width. */
  valueFontSize?: number;
}

/**
 * Draws a single Cyber Resilience card with a light band-tinted
 * background and dark band-coloured text. Used in both Section 04 and
 * Section 08 so the two visuals stay in lock-step.
 */
export function drawCyberResilienceCard(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  band: ResilienceBand,
  opts: CyberResilienceCardOptions = {},
): void {
  const tokens = BAND_COLORS[band] ?? BAND_COLORS.Unrated;
  const bgRgb = hexToRgb(tokens.bg);
  const textRgb = hexToRgb(tokens.text);
  const accentRgb = tokens.rgb;

  // Light tinted fill
  doc.setFillColor(bgRgb[0], bgRgb[1], bgRgb[2]);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  if (opts.topAccent !== false) {
    doc.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    doc.rect(x, y, w, 3, "F");
  }

  const value = String(band).toUpperCase();
  const label = (opts.label ?? "CYBER RESILIENCE").toUpperCase();

  const valueSize = opts.valueFontSize ?? (value.length > 8 ? 14 : 16);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(valueSize);
  doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
  doc.text(value, x + 10, y + Math.min(h - 22, 30));

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(textRgb[0], textRgb[1], textRgb[2]);
  doc.text(label, x + 10, y + h - 10);
}
